import React from 'react';
import { TopNavContext, useTopNavContext } from './TopNavContext';
import type { BoxProps } from '~components/Box';
import { BladeProvider, useTheme } from '~components/BladeProvider';
import BaseBox from '~components/Box/BaseBox';
import type { StyledPropsBlade } from '~components/Box/styledProps';
import {
  SIDE_NAV_EXPANDED_L1_WIDTH_XL,
  SIDE_NAV_EXPANDED_L1_WIDTH_BASE,
} from '~components/SideNav/tokens';
import { size } from '~tokens/global';
import { bladeTheme } from '~tokens/theme';
import { makeSize } from '~utils';
import { componentZIndices } from '~utils/componentZIndices';
import { makeAnalyticsAttribute } from '~utils/makeAnalyticsAttribute';
import { metaAttribute, MetaConstants } from '~utils/metaAttribute';
import type { DataAnalyticsAttribute, BladeElementRef, TestID } from '~utils/types';

const TOP_NAV_HEIGHT = size[56];

type TopNavProps = {
  children: React.ReactNode;
} & Pick<
  BoxProps,
  | 'padding'
  | 'paddingTop'
  | 'paddingBottom'
  | 'paddingLeft'
  | 'paddingRight'
  | 'paddingX'
  | 'paddingY'
  | 'backgroundColor'
  | 'position'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'width'
  | 'zIndex'
  | keyof DataAnalyticsAttribute
> &
  TestID &
  StyledPropsBlade;

const _TopNav = (
  { children, ...rest }: TopNavProps,
  ref: React.Ref<BladeElementRef>,
): React.ReactElement => {
  const { colorScheme } = useTheme();

  return (
    <TopNavContext.Provider value={{ colorScheme }}>
      {/* We are forcing the theme to dark here because the TopNav is always in dark mode. 
       we also want components inside the TopNav to be in the same theme as the TopNav.
      */}
      <BladeProvider themeTokens={bladeTheme} colorScheme="dark">
        <BaseBox
          ref={ref as never}
          display="grid"
          gridTemplateColumns="auto minmax(0, 1fr) auto"
          alignItems="center"
          position="sticky"
          top="0px"
          width="100%"
          paddingY={{ base: 'spacing.3', m: 'spacing.0' }}
          paddingX={{ base: 'spacing.4', m: 'spacing.3' }}
          height={makeSize(TOP_NAV_HEIGHT)}
          zIndex={componentZIndices.topnav}
          backgroundColor="interactive.background.staticBlack.default"
          {...rest}
          {...metaAttribute({ name: MetaConstants.TopNav, testID: rest.testID })}
          {...makeAnalyticsAttribute(rest)}
        >
          {children}
        </BaseBox>
      </BladeProvider>
    </TopNavContext.Provider>
  );
};

const TopNav = React.forwardRef(_TopNav);

const TopNavBrand = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  return (
    <BaseBox
      flexDirection="row"
      width={{
        base: makeSize(SIDE_NAV_EXPANDED_L1_WIDTH_BASE),
        xl: makeSize(SIDE_NAV_EXPANDED_L1_WIDTH_XL),
      }}
      {...metaAttribute({ name: MetaConstants.TopNavBrand })}
      paddingY="spacing.5"
      marginLeft="spacing.6"
    >
      <BaseBox width="100%">{children}</BaseBox>
    </BaseBox>
  );
};

const TopNavContent = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  return (
    <BaseBox
      display="flex"
      alignItems="center"
      alignSelf="center"
      justifyContent="center"
      {...metaAttribute({ name: MetaConstants.TopNavContent })}
    >
      {children}
    </BaseBox>
  );
};

const TopNavActions = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const topNavContext = useTopNavContext();
  const { colorScheme } = useTheme();
  const topNavActions = (
    <BaseBox
      alignSelf="flex-start"
      display="flex"
      gap="spacing.3"
      alignItems="center"
      marginTop="spacing.1"
      padding="spacing.3"
      paddingRight="spacing.2"
      borderTopLeftRadius="medium"
      borderTopRightRadius="medium"
      {...metaAttribute({ name: MetaConstants.TopNavActions })}
    >
      {children}
    </BaseBox>
  );

  if (topNavContext) {
    return topNavActions;
  }

  return (
    <TopNavContext.Provider value={{ colorScheme }}>
      <BladeProvider themeTokens={bladeTheme} colorScheme="dark">
        {topNavActions}
      </BladeProvider>
    </TopNavContext.Provider>
  );
};

export { TopNav, TopNavBrand, TopNavContent, TopNavActions };
export type { TopNavProps };
